/**
 * Side sheet (full screen on phones) with everything about one payment.
 */

import { useEffect, useState } from 'react';
import { ChevronDown, Copy, Gift, Loader2, Mail, RefreshCw, UserX, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { PaymentLinkPayment } from '@masonic-tools/shared-types';
import { copyTextToClipboard } from '@/lib/clipboard-utils';
import { showError, showSuccess } from '@/lib/swal';
import {
  fillGiftAidDeclaration,
  formatDateTime,
  formatPence,
  GIFT_AID_DECLARATION_TEMPLATE,
  METHOD_LABELS,
} from '@/lib/unit-payment-links';
import { callableErrorInfo, confirmPaymentLinkPayment, resendPaymentLinkReceipt } from '@/lib/payment-links-api';
import { PAYMENT_STATUS_LABELS, PAYMENT_STATUS_VARIANTS } from './PaymentsTable';

interface Props {
  payment: PaymentLinkPayment | null;
  unitName: string;
  onClose: () => void;
}

function Row({ label, children, mono }: { label: string; children: React.ReactNode; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className={cn('min-w-0 text-right', mono && 'break-all font-mono text-xs')}>{children}</dd>
    </div>
  );
}

function CopyButton({ value, label }: { value: string; label: string }) {
  return (
    <button
      type="button"
      className="ml-1 inline-flex rounded p-1 text-muted-foreground hover:text-foreground"
      aria-label={`Copy ${label}`}
      onClick={async () => ((await copyTextToClipboard(value)) ? showSuccess(`${label} copied`) : showError('Could not copy'))}
    >
      <Copy className="h-3.5 w-3.5" />
    </button>
  );
}

const SOURCE_LABELS: Record<string, string> = {
  checkout: 'checkout started',
  webhook: 'confirmed by provider',
  return: 'confirmed on return',
  sweep: 'checked automatically',
  manual: 'checked manually',
};

export function PaymentDetailDialog({ payment, unitName, onClose }: Props) {
  const [showDeclaration, setShowDeclaration] = useState(false);
  const [resending, setResending] = useState(false);
  const [checking, setChecking] = useState(false);

  useEffect(() => {
    if (!payment) return;
    setShowDeclaration(false);
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [payment, onClose]);

  if (!payment) return null;
  const p = payment;

  const providerIds: Array<[string, string | undefined]> = [
    ['Stripe payment', p.stripePaymentIntentId],
    ['Stripe checkout', p.stripeSessionId],
    ['SumUp transaction', p.sumupTransactionCode],
    ['SumUp checkout', p.sumupCheckoutId],
    ['SlickPay order', p.slickpayPoId],
  ];

  const resend = async () => {
    setResending(true);
    try {
      await resendPaymentLinkReceipt({ unitId: p.unitId, linkId: p.linkId, paymentId: p.id });
      showSuccess('Receipt sent');
    } catch (e) {
      showError(callableErrorInfo(e, 'Could not send the receipt').message);
    } finally {
      setResending(false);
    }
  };

  const checkStatus = async () => {
    setChecking(true);
    try {
      const r = await confirmPaymentLinkPayment({ unitId: p.unitId, linkId: p.linkId, paymentId: p.id, manual: true });
      showSuccess(r.status === 'pending' ? 'Still waiting for the provider' : `Payment is ${PAYMENT_STATUS_LABELS[r.status].toLowerCase()}`);
    } catch (e) {
      showError(callableErrorInfo(e, 'Could not check the payment').message);
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-sm" onClick={onClose} role="presentation">
      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="pl-payment-title"
        className="flex h-full w-full flex-col bg-background shadow-xl sm:max-w-md"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border p-5">
          <div>
            <p id="pl-payment-title" className="text-3xl font-bold tabular-nums">
              {formatPence(p.amountPence)}
            </p>
            <div className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
              <Badge variant={PAYMENT_STATUS_VARIANTS[p.status]}>{PAYMENT_STATUS_LABELS[p.status]}</Badge>
              <span>{formatDateTime(p.paidAt || p.createdAt)}</span>
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-sm p-1 opacity-70 hover:opacity-100" aria-label="Close">
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-6 overflow-y-auto p-5">
          <section>
            <h3 className="mb-1 text-sm font-semibold">Breakdown</h3>
            <dl className="divide-y divide-border">
              <Row label="Amount">{formatPence(p.amountPence)}</Row>
              <Row label="Processing fee">{formatPence(p.processingFeePence)}</Row>
              <Row label="Total charged">{formatPence(p.totalChargedPence)}</Row>
              <Row label="Net to unit">
                <strong>{formatPence(p.netToUnitPence)}</strong>
              </Row>
              <Row label="Fee mode">{p.feeMode === 'on_top' ? 'Added on top' : 'Included in amount'}</Row>
              {typeof p.creditsDebited === 'number' ? <Row label="Credits used">{p.creditsDebited}</Row> : null}
            </dl>
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold">Payer</h3>
            {p.anonymous ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <UserX className="h-4 w-4" /> Anonymous donor
              </p>
            ) : (
              <dl>
                <Row label="Name">
                  {p.payerName}
                  {p.payerName ? <CopyButton value={p.payerName} label="Name" /> : null}
                </Row>
                <Row label="Email">
                  {p.payerEmail ? (
                    <>
                      <a className="underline" href={`mailto:${p.payerEmail}`}>
                        {p.payerEmail}
                      </a>
                      <CopyButton value={p.payerEmail} label="Email" />
                    </>
                  ) : null}
                </Row>
              </dl>
            )}
            {p.message ? <blockquote className="mt-2 rounded-md bg-muted p-3 text-sm italic">“{p.message}”</blockquote> : null}
          </section>

          {p.giftAid ? (
            <section>
              <h3 className="mb-1 flex items-center gap-2 text-sm font-semibold">
                <Gift className="h-4 w-4 text-primary" /> Gift Aid declaration
              </h3>
              <dl className="divide-y divide-border">
                <Row label="Name">{[p.giftAid.title, p.giftAid.firstName, p.giftAid.lastName].filter(Boolean).join(' ')}</Row>
                <Row label="House">{p.giftAid.houseNameOrNumber}</Row>
                <Row label="Postcode">{p.giftAid.postcode}</Row>
                <Row label="Declared">{formatDateTime(p.giftAid.declaredAt)}</Row>
                <Row label="Eligible amount">{formatPence(p.giftAid.eligiblePence || p.amountPence)}</Row>
                <Row label="Wording version" mono>
                  {p.giftAid.declarationVersion}
                </Row>
              </dl>
              <button
                type="button"
                className="mt-2 flex items-center gap-1 text-sm font-medium text-primary"
                onClick={() => setShowDeclaration((v) => !v)}
                aria-expanded={showDeclaration}
              >
                View declaration wording <ChevronDown className={cn('h-4 w-4 transition-transform', showDeclaration && 'rotate-180')} />
              </button>
              {showDeclaration ? (
                <p className="mt-2 rounded-md bg-muted p-3 text-xs leading-relaxed text-muted-foreground">
                  {fillGiftAidDeclaration(GIFT_AID_DECLARATION_TEMPLATE, p.giftAid.eligiblePence || p.amountPence, unitName || 'the unit')}
                </p>
              ) : null}
            </section>
          ) : null}

          <section>
            <h3 className="mb-1 text-sm font-semibold">Payment details</h3>
            <dl className="divide-y divide-border">
              <Row label="Method">{METHOD_LABELS[p.method]}</Row>
              <Row label="Reference" mono>
                {p.reference}
              </Row>
              {providerIds
                .filter(([, v]) => !!v)
                .map(([label, v]) => (
                  <Row key={label} label={label} mono>
                    {v}
                    <CopyButton value={v!} label={label} />
                  </Row>
                ))}
              {p.closedReason ? <Row label="Reason">{p.closedReason}</Row> : null}
            </dl>
            {p.statusHistory.length > 0 ? (
              <ol className="mt-3 space-y-2 border-l-2 border-border pl-4">
                {p.statusHistory.map((h, i) => (
                  <li key={i} className="relative text-sm">
                    <span className="absolute -left-[1.4rem] top-1.5 h-2.5 w-2.5 rounded-full bg-primary" aria-hidden />
                    <span className="font-medium">{PAYMENT_STATUS_LABELS[h.status] || h.status}</span>{' '}
                    <span className="text-muted-foreground">
                      · {SOURCE_LABELS[h.source] || h.source} · {formatDateTime(h.at)}
                    </span>
                    {h.note ? <span className="block text-xs text-muted-foreground">{h.note}</span> : null}
                  </li>
                ))}
              </ol>
            ) : null}
          </section>

          <section>
            <h3 className="mb-1 text-sm font-semibold">Communications</h3>
            <ul className="space-y-1 text-sm text-muted-foreground">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {p.receiptSentAt
                  ? `Receipt sent ${formatDateTime(p.receiptSentAt)}${p.receiptSendCount > 1 ? ` (${p.receiptSendCount} times)` : ''}`
                  : p.payerEmail
                    ? p.status === 'paid'
                      ? 'Receipt not sent yet'
                      : 'Receipt is sent once paid'
                    : 'No receipt (no email given)'}
              </li>
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                {p.leadsNotifiedAt ? `Unit leads notified ${formatDateTime(p.leadsNotifiedAt)}` : 'Unit leads not notified for this payment'}
              </li>
            </ul>
          </section>
        </div>

        <div className="flex flex-col gap-2 border-t border-border p-4 sm:flex-row sm:justify-end">
          {p.status === 'pending' ? (
            <Button variant="outline" onClick={checkStatus} disabled={checking}>
              {checking ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Check status now
            </Button>
          ) : null}
          {p.status === 'paid' && p.payerEmail ? (
            <Button variant="outline" onClick={resend} disabled={resending || p.receiptSendCount >= 5}>
              {resending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}
              Resend receipt
            </Button>
          ) : null}
          <Button onClick={onClose}>Close</Button>
        </div>
      </aside>
    </div>
  );
}
